import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import React, { useMemo, useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { assignChecklist, getAllChecklists, getClientsMini } from "src/pages/api";

const REPEAT_OPTIONS = [
  { value: "Does not repeat", label: "Does not repeat" },
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
];

const formatTimeTo12h = (time24) => {
  if (!time24) return null;
  const [hours, minutes] = time24.split(":");
  const h = parseInt(hours, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${minutes} ${suffix}`;
};

const buildSchema = (isQrRequired) =>
  Yup.object().shape({
    checklistId: Yup.string().required("Checklist is required"),
    clients: Yup.array().min(1, "At least one client is required"),
    ...(isQrRequired
      ? {
          reminderDate: Yup.string().required("Date is required"),
          reminderTime: Yup.string().required("Time is required"),
          repeat: Yup.string().required("Repeat is required"),
        }
      : {}),
  });

export default function AssignChecklist({ onClose }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedChecklistId, setSelectedChecklistId] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientInputValue, setClientInputValue] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const clientSearchTimer = useRef(null);
  const [errors, setErrors] = useState({});

  // Reminder state
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [repeat, setRepeat] = useState("Does not repeat");
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [repeatEveryHrs, setRepeatEveryHrs] = useState("");
  const [maxTimesPerDay, setMaxTimesPerDay] = useState("");
  const [noExpiry, setNoExpiry] = useState(false);

  const handleClientInputChange = (_, value) => {
    setClientInputValue(value);
    clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => setClientSearch(value), 400);
  };

  const { data: checklistsData } = useQuery(
    ["checklists-all-mini"],
    ({ signal }) =>
      getAllChecklists({
        queryKey: ["checklists", 0, 500, "", ""],
        signal,
      }),
  );

  const { data: clientsRaw = [], isFetching: clientsLoading } = useQuery(
    ["clients-mini", clientSearch],
    ({ signal }) => getClientsMini({ signal, search: clientSearch, limit: 50 }),
    { keepPreviousData: true },
  );

  const { data: allClientsRaw = [], isFetching: allClientsLoading } = useQuery(
    ["clients-mini-all"],
    ({ signal }) => getClientsMini({ signal, limit: 10000 }),
    { keepPreviousData: true },
  );

  const clientOptions = useMemo(() => clientsRaw, [clientsRaw]);

  const checklistOptions = useMemo(() => checklistsData?.data || [], [checklistsData]);

  const selectedChecklist = useMemo(
    () => checklistOptions.find((c) => String(c.id) === selectedChecklistId) || null,
    [checklistOptions, selectedChecklistId],
  );

  // Check if selected checklist type is "Open without QR Code"
  const isWithoutQRCode = selectedChecklist?.type === "Open without QR Code";
  const isQrRequired = selectedChecklist
    ? selectedChecklist.type !== "Open without QR Code"
    : true;

  // Reset reminder fields when checklist changes to "Open without QR Code"
  const handleChecklistChange = (checklistId) => {
    setSelectedChecklistId(checklistId);
    // Reset all reminder fields
    setReminderDate("");
    setReminderTime("");
    setRepeat("Does not repeat");
    setShowMoreOptions(false);
    setRepeatEveryHrs("");
    setMaxTimesPerDay("");
    setNoExpiry(false);
    setErrors((prev) => ({
      ...prev,
      checklistId: undefined,
      reminderDate: undefined,
      reminderTime: undefined,
      repeat: undefined,
    }));
  };

  const mutation = useMutation(
    async () => {
      const clientIds = selectedClients.map((c) => Number(c.id));
      const companyId = selectedClients[0]?.companyId || null;
      const payload = {
        id: Number(selectedChecklistId),
        companyId,
        clientIds,
        startDate: isWithoutQRCode ? null : (reminderDate || null),
        time: isWithoutQRCode ? null : formatTimeTo12h(reminderTime),
        reminder: isWithoutQRCode ? null : repeat,
        repeat: isWithoutQRCode ? 0 : (repeatEveryHrs ? Number(repeatEveryHrs) : 0),
        perday: isWithoutQRCode ? 0 : (maxTimesPerDay ? Number(maxTimesPerDay) : 0),
        expiry: isWithoutQRCode ? 1 : (noExpiry ? 0 : 1),
      };
      return assignChecklist(payload);
    },
    {
      onSuccess: () => {
        enqueueSnackbar("Checklist assigned successfully", { variant: "success" });
        queryClient.invalidateQueries(["checklists"]);
        onClose();
      },
      onError: (error) => {
        const apiMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.response?.data?.message;
        enqueueSnackbar(apiMessage || error?.message || "Failed to assign checklist", {
          variant: "error",
        });
      },
    },
  );

  const handleSubmit = async () => {
    try {
      await buildSchema(isQrRequired).validate(
        { 
          checklistId: selectedChecklistId, 
          clients: selectedClients, 
          reminderDate, 
          reminderTime, 
          repeat 
        },
        { abortEarly: false }
      );
      setErrors({});
      mutation.mutate();
    } catch (err) {
      const fieldErrors = {};
      err.inner.forEach((e) => { fieldErrors[e.path] = e.message; });
      setErrors(fieldErrors);
    }
  };

  return (
    <FormWrapper
      onClose={onClose}
      title="Assign Checklist"
      maxWidth="sm"
      submitText="Assign Checklist"
      onSubmit={handleSubmit}
      loading={mutation.isLoading}
      hiddenReset
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            Checklist Name *
          </Typography>
          <TextField
            select
            fullWidth
            value={selectedChecklistId}
            onChange={(e) => handleChecklistChange(e.target.value)}
            placeholder="Select Checklist"
            error={!!errors.checklistId}
          >
            <MenuItem value="" disabled>
              Select Checklist
            </MenuItem>
            {checklistOptions.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          {errors.checklistId && <FormHelperText error>{errors.checklistId}</FormHelperText>}
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography variant="subtitle2">
              Assign Client *
            </Typography>
            {selectedClients.length > 0 && (
              <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", bgcolor: "primary.lighter", px: 1, py: 0.3, borderRadius: 1 }}>
                {selectedClients.length} selected
              </Typography>
            )}
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                indeterminate={selectedClients.length > 0 && selectedClients.length < allClientsRaw.length}
                checked={allClientsRaw.length > 0 && selectedClients.length === allClientsRaw.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedClients(allClientsRaw);
                  } else {
                    setSelectedClients([]);
                  }
                  setErrors((prev) => ({ ...prev, clients: undefined }));
                }}
                size="small"
                disabled={allClientsLoading}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Select All {allClientsLoading ? "" : `(${allClientsRaw.length})`}
                {allClientsLoading && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
              </Typography>
            }
            sx={{ mb: 0.5, ml: 0 }}
          />
          <Autocomplete
            multiple
            options={clientOptions}
            loading={clientsLoading}
            disableCloseOnSelect
            value={selectedClients}
            onChange={(_, value) => {
              setSelectedClients(value);
              setErrors((prev) => ({ ...prev, clients: undefined }));
            }}
            inputValue={clientInputValue}
            onInputChange={handleClientInputChange}
            getOptionLabel={(opt) => opt?.clientName || ""}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            noOptionsText="No clients found"
            filterOptions={(x) => x}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.clientName || ""}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search client..."
                error={!!errors.clients}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {errors.clients && <FormHelperText error>{errors.clients}</FormHelperText>}
        </Box>

        {/* Reminder Section - Only show if NOT "Open without QR Code" */}
        {!isWithoutQRCode && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Reminder {isQrRequired ? "*" : ""}
            </Typography>
            <Stack spacing={1.5}>
              {/* Date + Time row */}
              <Stack direction="row" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    type="date"
                    fullWidth
                    value={reminderDate}
                    onChange={(e) => {
                      setReminderDate(e.target.value);
                      setErrors((prev) => ({ ...prev, reminderDate: undefined }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ placeholder: "dd/mm/yyyy" }}
                    error={!!errors.reminderDate}
                  />
                  {errors.reminderDate && <FormHelperText error>{errors.reminderDate}</FormHelperText>}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    type="time"
                    fullWidth
                    value={reminderTime}
                    onChange={(e) => {
                      setReminderTime(e.target.value);
                      setErrors((prev) => ({ ...prev, reminderTime: undefined }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.reminderTime}
                  />
                  {errors.reminderTime && <FormHelperText error>{errors.reminderTime}</FormHelperText>}
                </Box>
              </Stack>

              {/* Repeat dropdown */}
              <TextField
                select
                fullWidth
                value={repeat}
                onChange={(e) => {
                  setRepeat(e.target.value);
                  setErrors((prev) => ({ ...prev, repeat: undefined }));
                }}
                error={!!errors.repeat}
              >
                {REPEAT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              {errors.repeat && <FormHelperText error>{errors.repeat}</FormHelperText>}

              <Box
                onClick={() => setShowMoreOptions((prev) => !prev)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  border: showMoreOptions ? "2px solid" : "1px solid",
                  borderColor: showMoreOptions ? "primary.main" : "divider",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  userSelect: "none",
                }}
              >
                {showMoreOptions ? (
                  <ExpandMoreIcon fontSize="small" sx={{ mr: 0.5 }} />
                ) : (
                  <ChevronRightIcon fontSize="small" sx={{ mr: 0.5 }} />
                )}
                <Typography variant="body2">More options</Typography>
              </Box>

              {/* Expanded more options */}
              <Collapse in={showMoreOptions}>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                  <TextField
                    fullWidth
                    placeholder="Repeat every X hrs"
                    type="number"
                    value={repeatEveryHrs}
                    onChange={(e) => setRepeatEveryHrs(e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    fullWidth
                    placeholder="Max Y times/day"
                    type="number"
                    value={maxTimesPerDay}
                    onChange={(e) => setMaxTimesPerDay(e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={noExpiry}
                        onChange={(e) => setNoExpiry(e.target.checked)}
                      />
                    }
                    label="No expiry"
                  />
                </Stack>
              </Collapse>
            </Stack>
          </Box>
        )}
      </Stack>
    </FormWrapper>
  );
}